/**
 * Компактный список.
 *
 * Самое плотное представление: одна строка на элемент, максимум элементов
 * на экране. Нужно, когда важно охватить объём, а не сравнивать поля —
 * для сравнения есть таблица.
 */

import { FlagIcon } from 'lucide-react';
import type { ItemSummaryDto } from '@project-os/contracts';
import { ITEM_TYPE_LABELS_RU } from '@project-os/contracts';
import { ItemStatusBadge } from '@/components/common/status-badge';
import { UserAvatar } from '@/components/common/user-avatar';
import { formatDate, formatRelative } from '@/lib/format';
import { cn } from '@/lib/utils';
import { ItemTypeIcon } from '../item-icon';

export function ItemsList({
  items,
  selectedId,
  onSelect,
}: {
  items: ItemSummaryDto[];
  selectedId: string | null;
  onSelect: (itemId: string) => void;
}) {
  return (
    <ul className="divide-y divide-border/50 px-3">
      {items.map((item) => (
        <li key={item.id}>
          <button
            type="button"
            onClick={() => onSelect(item.id)}
            className={cn(
              'row-hover flex w-full items-center gap-2.5 rounded-md px-3 py-1.5 text-left',
              selectedId === item.id && 'bg-accent',
              item.archivedAt && 'opacity-55',
            )}
          >
            <ItemTypeIcon type={item.type} className="size-3.5" />

            {item.isNextAction ? (
              <FlagIcon className="size-3 shrink-0 text-primary" aria-label="Следующее действие" />
            ) : null}

            <span className="min-w-0 flex-1 truncate text-sm">{item.title}</span>

            <span className="hidden w-28 shrink-0 truncate text-2xs text-muted-foreground lg:block">
              {ITEM_TYPE_LABELS_RU[item.type]}
            </span>

            {item.dueDate ? (
              <span className="hidden w-24 shrink-0 text-2xs text-muted-foreground sm:block">
                {formatDate(item.dueDate)}
              </span>
            ) : (
              <span className="hidden w-24 shrink-0 sm:block" />
            )}

            <span className="w-32 shrink-0">
              <ItemStatusBadge type={item.type} status={item.status} />
            </span>

            <span className="hidden w-6 shrink-0 md:block">
              {item.owner ? (
                <UserAvatar user={item.owner} size="sm" title={item.owner.name} />
              ) : null}
            </span>

            <span className="hidden w-24 shrink-0 text-right text-2xs text-muted-foreground xl:block">
              {formatRelative(item.updatedAt)}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
